import type {AutoDeliveryDcxUpdateReasonStatus} from '@/consts/icc/iccAutoDeliveryStatusTypes';
import {AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES} from '@/consts/icc/iccAutoDeliveryStatusTypes';
import appContent from '@/services/content/appContent.typed';
import type {InlineNotificationModel} from '@/types/aem';
import type {AutoDeliveryInfoBoxData, AutoDeliveryInfoBoxSource} from '@/types/content';
import {AUTO_DELIVERY_INFO_BOX_SOURCES} from '@/types/content';

const ON_HOLD_CONTENT_VARIANTS = {
    PAYMENT_FAILURE: 'paymentFailure',
    PRICE_CHANGED: 'priceChanged',
} as const;

type OnHoldContentVariant = (typeof ON_HOLD_CONTENT_VARIANTS)[keyof typeof ON_HOLD_CONTENT_VARIANTS];

const PRICE_CHANGE_REASONS = new Set<string>([
    AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.AD_HOLD_02,
    AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.AD_HOLD_05,
    AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.AD_HOLD_06,
]);

const getOnHoldContentVariant = (
    statusUpdateReason: AutoDeliveryDcxUpdateReasonStatus | null
): OnHoldContentVariant => {
    if (statusUpdateReason && PRICE_CHANGE_REASONS.has(statusUpdateReason)) {
        return ON_HOLD_CONTENT_VARIANTS.PRICE_CHANGED;
    }

    return ON_HOLD_CONTENT_VARIANTS.PAYMENT_FAILURE;
};

export const getOnHoldInfoBoxData = (
    statusUpdateReason: AutoDeliveryDcxUpdateReasonStatus | null,
    source: AutoDeliveryInfoBoxSource
): AutoDeliveryInfoBoxData | undefined => {
    const variant = getOnHoldContentVariant(statusUpdateReason);

    const content = appContent.getAutoDeliveryContent();
    const isDashboard = source === AUTO_DELIVERY_INFO_BOX_SOURCES.DASHBOARD;

    const buildResult = (notification: InlineNotificationModel | undefined): AutoDeliveryInfoBoxData | undefined => {
        if (!notification) return undefined;

        return {notification};
    };

    switch (variant) {
        case ON_HOLD_CONTENT_VARIANTS.PAYMENT_FAILURE:
            return buildResult(
                isDashboard
                    ? content?.dashboardContent?.onHoldOrderPaymentFailureIssueDashboard?.inlineNotification
                    : content?.extendedDetailsContent?.onHoldOrderPaymentFailureIssue?.orderDetailsInlineNotification
            );
        case ON_HOLD_CONTENT_VARIANTS.PRICE_CHANGED:
            return buildResult(
                isDashboard
                    ? content?.dashboardContent?.onHoldOrderPaymentPriceChangedDashboard?.inlineNotification
                    : content?.extendedDetailsContent?.onHoldOrderPaymentPriceChanged?.orderDetailsInlineNotification
            );
        default:
            return undefined;
    }
};
